import type { IndividualIdleClip } from './individual-idle-assets';

export const CALM_NPC_IDLE_CLIPS = ['catwalk_idle'] as const;
export const BROAD_NPC_IDLE_CLIPS = ['bored', 'idle', 'looking_around'] as const;

/** One presentation clock per actor; called by the existing renderer RAF only. */
export class NPCIdleScheduler {
  private clip: IndividualIdleClip | null = null;
  private start = 0;
  private nextGesture = 0;
  private lastSample: number | null = null;
  private broad = false;
  private waitingForBase = false;
  private quietInterval = 0;
  private pendingGesture: IndividualIdleClip = 'bored';
  private preloadingGesture = false;
  private seed: number;

  constructor(actorKey: string, private duration: (clip: IndividualIdleClip) => number) {
    this.seed = [...actorKey].reduce((hash, char) => Math.imul(hash, 31) + char.charCodeAt(0) | 0, 17) >>> 0;
  }

  private random(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 0x100000000;
  }

  private quiet(now: number): void {
    this.clip = CALM_NPC_IDLE_CLIPS[Math.floor(this.random() * CALM_NPC_IDLE_CLIPS.length)]!;
    this.broad = false;
    this.start = now;
    this.waitingForBase = true;
    this.preloadingGesture = false;
    this.quietInterval = 20_000 + this.random() * 20_000;
    this.pendingGesture = BROAD_NPC_IDLE_CLIPS[Math.floor(this.random() * BROAD_NPC_IDLE_CLIPS.length)]!;
    const loop = this.duration(this.clip);
    // Complete the calm loop before a gesture; never cut it mid-pose.
    this.nextGesture = now + Math.ceil(this.quietInterval / loop) * loop;
  }

  sample(now: number, isReady: (clip: IndividualIdleClip) => boolean = () => true): { clip: IndividualIdleClip; elapsedMs: number } {
    if (!this.clip || (this.lastSample !== null && (now < this.lastSample || now - this.lastSample > 5_000))) this.quiet(now);
    this.lastSample = now;
    if (this.broad && now - this.start >= this.duration(this.clip!)) this.quiet(now);
    if (this.waitingForBase) {
      if (!isReady(this.clip!)) return { clip: this.clip!, elapsedMs: 0 };
      this.waitingForBase = false; this.start = now;
      const loop = this.duration(this.clip!);
      this.nextGesture = now + Math.ceil(this.quietInterval / loop) * loop;
    }
    if (!this.broad && (this.preloadingGesture || now >= this.nextGesture - 1000)) {
      this.preloadingGesture = true;
      const ready = isReady(this.pendingGesture); // Demand the next source before the loop boundary.
      if (now >= this.nextGesture) {
        if (ready) { this.clip = this.pendingGesture; this.broad = true; this.preloadingGesture = false; this.start = now; }
        else this.nextGesture += this.duration(this.clip!); // Keep a full calm loop while decode/retry waits.
      }
    }
    return { clip: this.clip!, elapsedMs: Math.max(0, now - this.start) };
  }
}
